import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IonIcon, IonItem, IonLabel, IonList } from '@ionic/react'
import { informationCircle } from 'ionicons/icons'
import CommentIcon from '../../../../components/commentSection/commentIcon/CommentIcon.component'
import SegmentedSelect, { ISegmentData } from '../../../../components/segmentedSelect/SegmentedSelect.component'
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteBodySurveyExam,
  setExaminationInProgress,
  setSelectedBodyPartData,
  updateExamPNNVValue,
  updateExamValue,
} from '../bodySurvey/store/bodySurvey.actions'
import { getBodyAssessmentSelector } from '../selectors/getBodyAssessment.selector'
import { getBodySurveyOptionsSelector } from '../../../options/selectors'
import {
  checkBodyPartsAndDataMatch,
  checkSelectedBodyPartsHasSameComments,
  examMapping,
  filterCommonNamedOptions,
  groupCommonOptions,
  reverseTransformationOnNeuroMentalData,
  segregateOptions,
} from '../../../../utilities/bodySurvey.util'
import { BodyAssessmentConstants } from '../store/bodyAssessment.constants'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import {
  IBodyParts,
  IBodySurveyState,
  IExamData,
  IExamValue,
  IExamValueOption,
} from '../bodySurvey/store/bodySurvey.types'
import { showToast } from '../../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../../components/toast/store/toast.constants'
import { BodySurveyConstants } from '../bodySurvey/store/bodySurvey.constants'
import { IOption, IOptions } from '../../../../common/common.types'
import './AssessmentFindings.component.css'

const segmentsMetaData: ISegmentData[] = [
  {
    id: 1,
    name: 'Present',
    type: 'multi',
    color: 'var(--ion-color-zoll-purple-contrast)',
  },
  {
    id: 2,
    name: 'Not Present',
    type: 'multi',
    color: 'var(--ion-color-zoll-golden-brown-contrast)',
  },
]

export interface IAssessmentFindingsProps {
  showCommentsIcon?: boolean
  handleCommentClick?: (() => void) | null
  bodySurvey: IBodySurveyState
}

function filterPartsBasedOnLocationIds(data: IExamData[], filterIds: number[]): IExamData[] {
  const filterData = data.filter((d) => filterIds.includes(d.locationId))

  const groupedByName = filterData.reduce((acc: { [key: string]: IExamData[] }, item) => {
    if (!acc[item.name]) {
      acc[item.name] = []
    }
    acc[item.name].push(item)
    return acc
  }, {})

  const result = Object.values(groupedByName)
    .filter((group) => {
      const locationIdsInGroup = new Set(group.map((item) => item.locationId))
      return filterIds.every((id) => locationIdsInGroup.has(id))
    })
    .flat()

  return result.length ? result : []
}

const AssessmentFindingsComponent: React.FC<IAssessmentFindingsProps> = ({
  showCommentsIcon = true,
  handleCommentClick = null,
  bodySurvey,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [displayOptions, setDisplayOptions] = useState<IExamValue[]>([])
  const [options, setOptions] = useState<IExamData[]>([])

  const { selectedExam, bodySelectionMode } = useSelector(getBodyAssessmentSelector)
  const bodySurveyOptions = useSelector(getBodySurveyOptionsSelector)

  const { examValuesPNNVs, examValues, __selectedBodyParts, findings, __examinationInProgress } = bodySurvey

  const examNameMapping: Record<string, string> = {
    [BodyAssessmentConstants.BODY_PARTS]: t('additionalAssessments.bodySurvey.bodyPartsComments'),
    [BodyAssessmentConstants.GENERAL_EXTREMITIES]: t('additionalAssessments.bodySurvey.generalExtremitiesComments'),
    [BodyAssessmentConstants.EXTERNAL_SKIN]: t('additionalAssessments.bodySurvey.externalSkinComments'),
    [BodyAssessmentConstants.CARDIO_CONDITIONS]: t('additionalAssessments.bodySurvey.cardioConditionsComments'),
    [BodyAssessmentConstants.MENTAL_CONDITIONS]: t('additionalAssessments.bodySurvey.mentalConditionsComments'),
    [BodyAssessmentConstants.NEURO_CONDITIONS]: t('additionalAssessments.bodySurvey.neuroConditionsComments'),
  }

  const setAssessmentOptions = (assessmentOptions: IExamValue[]) => {
    const groupedByName = filterCommonNamedOptions(assessmentOptions)
    const groupedFilterOptions = groupCommonOptions(groupedByName.duplicates)
    const updatedDisplayOptions = [...groupedByName.nonDuplicates, ...groupedFilterOptions]
    setOptions(updatedDisplayOptions)
  }

  const handleBodyPartsFindings = () => {
    const removeZoomedParts = __selectedBodyParts.filter((bodyPart: IBodyParts) => !bodyPart.hasZoom)
    const findingIds = removeZoomedParts.map((item: IBodyParts) => item.id)
    const customResponse = filterPartsBasedOnLocationIds(bodySurveyOptions.bodyPartsOptions, findingIds)
    const finalData = groupCommonOptions(customResponse)
    setOptions(finalData)
  }

  const getLocationData = () => {
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY) {
      switch (bodySelectionMode?.id) {
        case 1:
          return __selectedBodyParts
        case 2:
          return examMapping[BodyAssessmentConstants.EXTERNAL_SKIN]
        case 3:
          return examMapping[BodyAssessmentConstants.GENERAL_EXTREMITIES]
        default:
          return examMapping[selectedExam]
      }
    } else {
      return examMapping[selectedExam]
    }
  }

  const locationData: IOptions = getLocationData()

  const setOptionsForBodySurvey = () => {
    if (bodySelectionMode.id === 1) {
      handleBodyPartsFindings()
    } else if (bodySelectionMode.id === 2) {
      setAssessmentOptions(bodySurveyOptions.skinOptions)
    } else if (bodySelectionMode.id === 3) {
      setAssessmentOptions(bodySurveyOptions.generalExtremitiesOptions)
    }
  }

  const fetchOptions = () => {
    switch (selectedExam) {
      case BodyAssessmentConstants.BODY_SURVEY:
        setOptionsForBodySurvey()
        break
      case BodyAssessmentConstants.MENTAL_CONDITIONS:
        setAssessmentOptions(bodySurveyOptions.mentalExamOptions)
        break
      case BodyAssessmentConstants.NEURO_CONDITIONS:
        setAssessmentOptions(bodySurveyOptions.neuroExamOptions)
        break
      case BodyAssessmentConstants.CARDIO_CONDITIONS:
        setAssessmentOptions(bodySurveyOptions.cardiacExamOptions)
        break
      default:
        break
    }
  }

  useEffect(() => {
    fetchOptions()
  }, [])

  useEffect(() => {
    const combinedExamOptions = [...examValues, ...examValuesPNNVs]
    const filterExamValuesForLocations = combinedExamOptions.filter((exam) =>
      locationData?.some((part) => part.id === exam.locationId)
    )
    const filterAndGroupOptions = (transformFunction: ((data: IExamValue[]) => any[]) | null) => {
      const filteredOptions = transformFunction
        ? transformFunction(filterExamValuesForLocations)
        : filterExamValuesForLocations
      const groupedByName = filterCommonNamedOptions(filteredOptions)
      const groupedFilterOptions = groupCommonOptions(groupedByName.duplicates)
      return [...groupedByName.nonDuplicates, ...groupedFilterOptions]
    }
    const dataMatches = checkBodyPartsAndDataMatch(__selectedBodyParts, combinedExamOptions)
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY && bodySelectionMode.id === 1) {
      if (filterExamValuesForLocations.length === 0) {
        setSelectedBodyPartData([])
      }
      if ((!dataMatches && !__examinationInProgress) || filterExamValuesForLocations.length === 0) {
        setDisplayOptions([])
      } else if (dataMatches) {
        const updatedDisplayOptions = filterAndGroupOptions(null)
        setDisplayOptions(updatedDisplayOptions)
      }
    } else {
      const transformFunction =
        selectedExam === BodyAssessmentConstants.MENTAL_CONDITIONS ||
        selectedExam === BodyAssessmentConstants.NEURO_CONDITIONS
          ? reverseTransformationOnNeuroMentalData
          : null

      const updatedDisplayOptions = filterAndGroupOptions(transformFunction)
      setDisplayOptions(updatedDisplayOptions)
    }
    if (!__examinationInProgress) {
      fetchOptions()
    }
  }, [bodySelectionMode, locationData, examValues, examValuesPNNVs, selectedExam])

  const handleChange = (values: IExamValueOption[], _?: ISegmentData, selectedOption?: IOption) => {
    dispatch(setExaminationInProgress(true))

    const { presentPickList, absencePickList } = filterValues(values)

    const hasPicklistOptionRemoved = !values.some((val) => val.id === selectedOption?.id)

    if (!hasPicklistOptionRemoved) {
      if (values.every((value) => value.originalLocationIds)) {
        handleGroupedExams(presentPickList, absencePickList)
      } else {
        handleUngroupedExams(presentPickList, absencePickList)
      }
    } else {
      dispatch(deleteBodySurveyExam(selectedOption))
    }

    updateBodyPartFindings()

    function filterValues(values: IExamValueOption[]) {
      const presentPickList = values.filter((item) => item.segmentId === 1)
      const absencePickList = values.filter((item) => item.segmentId === 2)
      return { presentPickList, absencePickList }
    }

    function handleGroupedExams(presentPickList: IExamValueOption[], absencePickList: IExamValueOption[]) {
      const segregatedPresentValues = segregateOptions(presentPickList, locationData)
      const segregatedAbsentValues = segregateOptions(absencePickList, locationData)

      handleToastMessages(segregatedPresentValues, segregatedAbsentValues)

      dispatch(updateExamValue(segregatedPresentValues))
      dispatch(updateExamPNNVValue(segregatedAbsentValues))
    }

    function handleUngroupedExams(presentPickList: IExamValueOption[], absencePickList: IExamValueOption[]) {
      presentPickList.forEach(assignLocationName)
      absencePickList.forEach(assignLocationName)

      dispatch(updateExamValue(presentPickList))
      dispatch(updateExamPNNVValue(absencePickList))
    }

    function assignLocationName(exam: IExamValueOption) {
      const location = locationData.find((loc) => loc.id === exam.locationId)
      if (location?.name) {
        exam.__locationName = location.name
      }
    }

    function handleToastMessages(segregatedPresentValues: IExamValue[], segregatedAbsentValues: IExamValue[]) {
      const absentValueInPresent = examValues.find((val) =>
        segregatedAbsentValues.some((segAbsVal) => segAbsVal.id === val.id)
      )
      const presentValueInAbsent = examValuesPNNVs.find((val) =>
        segregatedPresentValues.some((segPresVal) => segPresVal.id === val.id)
      )

      if (absentValueInPresent) {
        dispatch(
          showToast({
            message: `${t('additionalAssessments.notifications.clearExaminationForPresentValue')} ${
              absentValueInPresent.name
            } in ${absentValueInPresent.__locationName}`,
            position: 'top',
            color: ToastColor.Warning,
          })
        )
      }
      if (presentValueInAbsent) {
        dispatch(
          showToast({
            message: `${t('additionalAssessments.notifications.clearExaminationForAbsentValue')} ${
              presentValueInAbsent.name
            } in ${presentValueInAbsent.__locationName}`,
            position: 'top',
            color: ToastColor.Warning,
          })
        )
      }
    }

    function updateBodyPartFindings() {
      const updatedBodyPartFindings = __selectedBodyParts.map((part) => ({
        ...part,
        isExamined: true,
      }))
      dispatch(setSelectedBodyPartData(updatedBodyPartFindings))
    }
  }

  const infoPopupContent = () => {
    return (
      <IonList>
        <IonItem lines='none'>
          <IonIcon className='info-icon-margin' slot='start' icon={informationCircle}></IonIcon>
          <IonLabel>
            <h1>{t('bodyAssessment.PresentVsNotPresent')}</h1>
          </IonLabel>
        </IonItem>
        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('bodyAssessment.Present')}</h3>
            <p className='break-space'>{t('bodyAssessment.PresentDescription')}</p>
          </IonLabel>
        </IonItem>
        <hr className='field-divider' />
        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('bodyAssessment.NotPresent')}</h3>
            <p className='break-space'>{t('bodyAssessment.NotPresentDescription')}</p>
          </IonLabel>
        </IonItem>
      </IonList>
    )
  }

  const onCommentClick = () => {
    if (handleCommentClick) handleCommentClick()
  }

  const getCommentPageTitle = () => {
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY) {
      const modeMapping: Record<number, BodyAssessmentConstants> = {
        1: BodyAssessmentConstants.BODY_PARTS,
        2: BodyAssessmentConstants.EXTERNAL_SKIN,
        3: BodyAssessmentConstants.GENERAL_EXTREMITIES,
      }
      return examNameMapping[modeMapping[bodySelectionMode?.id as number] || ''] || ''
    }
    return examNameMapping[selectedExam] || ''
  }

  const getCommentSection = () => {
    if (!showCommentsIcon) return null
    const hasAllSameComments = checkSelectedBodyPartsHasSameComments(__selectedBodyParts, findings)
    const hideComments = __selectedBodyParts.length > 1 && bodySelectionMode.id === 1 && !hasAllSameComments

    const filterFindingsBasedOnLocation = findings?.filter(
      (exam) => locationData && locationData.some((part) => part.id === exam.locationId)
    )
    const commentProps = handleCommentClick
      ? { onClick: onCommentClick }
      : { onChangeAction: [BodySurveyConstants.SET_COMMENTS, '__comments'] }

    return (
      <CommentIcon
        commentPageTitle={getCommentPageTitle()}
        className='pl-8'
        commentText={filterFindingsBasedOnLocation[0]?.comments || ''}
        commentMaxLength={4000}
        previousPagePath={TabConstants.BODY_ASSESSMENT}
        hideComments={hideComments}
        {...commentProps}
      />
    )
  }

  return (
    <>
      <div id='presence-absence-toggle-row' className='ion-no-padding'>
        <SegmentedSelect
          options={options}
          segmentsData={segmentsMetaData}
          values={displayOptions}
          onChange={handleChange}
          infoPopupContent={infoPopupContent()}
          commentContent={getCommentSection()}
        />
        <hr className='field-divider' />
      </div>
    </>
  )
}

export default AssessmentFindingsComponent
